import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { ApiResponse, MyExperience } from '@core/models';

@Injectable({
  providedIn: 'root'
})
export class MyExperienceService {
  private apiUrl = `${environment.apiUrl}/experiences`;

  private readonly http = inject(HttpClient);

  /**
   * Add a new experience with image
   * @param experience MyExperience data to be added
   * @param imageFile Optional image file to upload
   */
  addExperience(experience: Omit<MyExperience, 'id' | 'image'>, imageFile?: File): Observable<MyExperience> {
    const formData = new FormData();

    Object.keys(experience).forEach(key => {
      formData.append(key, experience[key]);
    });

    if (imageFile) {
      formData.append('image', imageFile);
    }

    return this.http.post<MyExperience>(this.apiUrl, formData);
  }

  /**
   * Update an existing experience
   * @param id MyExperience ID
   * @param experience Updated experience data
   * @param imageFile Optional new image file
   */
  updateExperience(id: number, experience: Partial<MyExperience>, imageFile?: File): Observable<MyExperience> {
    const formData = new FormData();

    formData.append('id', id.toString());

    Object.keys(experience).forEach(key => {
      formData.append(key, experience[key]);
    });

    if (imageFile) {
      formData.append('image', imageFile);
    }

    return this.http.put<MyExperience>(`${this.apiUrl}`, formData);
  }

  /**
   * Delete an experience
   * @param id MyExperience ID to be deleted
   */
  deleteExperience(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get all experiences
   */
  getAllExperiences(): Observable<MyExperience[]> {
    return this.http.get<MyExperience[]>(this.apiUrl);
  }

  /**
   * Get a single experience by ID
   * @param id MyExperience ID
   */
  getExperienceById(id: string): Observable<ApiResponse<MyExperience>> {
    return this.http.get<ApiResponse<MyExperience>>(`${this.apiUrl}/${id}`);
  }
}
